/** Configs do grupo "Categorias" — 4 CRUDs de categoria (fleet/personnel/places/assets) + Status universal. */
/* eslint-disable react-refresh/only-export-components -- configs co-locam os componentes Fields propositalmente */
import { useState } from 'react'
import { z } from 'zod'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { StatusChip } from '../../../shared/components/StatusChip'
import { RHFText, RHFSwitch } from '../../../shared/form/fields'
import type { ResourceConfig, ResourceFilters } from '../config'
import {
  fleetCategoriesResource, personnelCategoriesResource, placeCategoriesResource, assetCategoriesResource,
  modalityCategoriesResource, statusResource,
} from '../resources'
import type { Resource } from '../../../core/api/resource'
import type { Categoria, Status } from '../../../core/api/types'
import { useDeclarativeText } from '../../../shared/i18n/declarativeText'

// ── Categorias (fábrica reaproveitada pelas 4 telas) ─────────────────
const categorySchema = z.object({
  code: z.string().min(1, 'Informe o código'),
  label: z.string().min(1, 'Informe o rótulo'),
  sort_order: z.string().optional(),
  active: z.boolean(),
})

function CategoryFields() {
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="code" label="Código" required />
        <RHFText name="sort_order" label="Ordem" type="number" />
      </Box>
      <RHFText name="label" label="Rótulo" required />
      <RHFSwitch name="active" label="Ativa" />
    </>
  )
}

function makeCategoryConfig(
  title: string, singular: string, basePath: string, resource: Resource<Categoria>,
  exportPrefix: string,
): ResourceConfig<Categoria> {
  return {
    title, eyebrow: 'Categorias', singular, basePath, exportPrefix, labelOf: (c) => c.label || c.code,
    // Categoria é catálogo compartilhado: a LEITURA não é gateada no servidor,
    // porque filtrar não protege nada e quebraria todo formulário que a usa como
    // seletor. A escrita é, e é ela que estes três controlam.
    createPermission: 'master_data.create',
    editPermission: 'master_data.edit',
    deletePermission: 'master_data.delete',
    resource, searchable: true, schema: categorySchema, Fields: CategoryFields, formMaxWidth: 480,
    columns: [
      { key: 'code', header: 'Código', sortable: true },
      { key: 'label', header: 'Rótulo', sortable: true },
      { key: 'sort_order', header: 'Ordem', align: 'right', sortable: true, render: (c) => c.sort_order ?? '—' },
      { key: 'active', header: 'Situação', render: (c) => <StatusChip label={c.active === false ? 'Inativa' : 'Ativa'} tone={c.active === false ? 'neutral' : 'success'} /> },
    ],
    emptyForm: { code: '', label: '', sort_order: '', active: true },
    toForm: (c) => ({ code: c.code, label: c.label, sort_order: c.sort_order != null ? String(c.sort_order) : '', active: c.active !== false }),
    toBody: (v) => ({ code: v.code, label: v.label, sort_order: v.sort_order === '' ? null : Number(v.sort_order), active: v.active }),
  }
}

// O prefixo do arquivo repete o nome da grade: as cinco planilhas têm as mesmas
// quatro colunas, e sem nome próprio chegariam iguais na pasta de quem baixou.
export const fleetCategoriesConfig = {
  ...makeCategoryConfig(
    'Tipos de veículo', 'tipo de veículo', '/logistica/categorias/fleet', fleetCategoriesResource,
    'categorias-veiculo',
  ),
  readOnly: true,
}
export const personnelCategoriesConfig = makeCategoryConfig(
  'Categorias de pessoas', 'categoria de pessoa', '/logistica/categorias/personnel', personnelCategoriesResource,
  'categorias-pessoa',
)
export const placeCategoriesConfig = makeCategoryConfig(
  'Categorias de locais', 'categoria de local', '/logistica/categorias/places', placeCategoriesResource,
  'categorias-local',
)
export const assetCategoriesConfig = makeCategoryConfig(
  'Categorias de produtos', 'categoria de produto', '/logistica/categorias/assets', assetCategoriesResource,
  'categorias-produto',
)
export const modalityCategoriesConfig = makeCategoryConfig(
  'Modalidades', 'modalidade', '/logistica/categorias/modality', modalityCategoriesResource,
  'categorias-modalidade',
)

// ── Status universal ─────────────────────────────────────────────────
const statusSchema = z.object({
  context: z.string().min(1, 'Informe o contexto'),
  code: z.string().min(1, 'Informe o código'),
  label_pt: z.string().min(1, 'Informe o rótulo (pt)'),
  label_en: z.string().min(1, 'Informe o rótulo (en)'),
  sort_order: z.string().optional(),
  active: z.boolean(),
})

function StatusFields() {
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 2 }}>
        <RHFText name="context" label="Contexto" required placeholder="ticket, shipment…" />
        <RHFText name="code" label="Código" required />
        <RHFText name="sort_order" label="Ordem" type="number" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="label_pt" label="Rótulo (pt)" required />
        <RHFText name="label_en" label="Rótulo (en)" required />
      </Box>
      <RHFSwitch name="active" label="Ativo" />
    </>
  )
}

/** Filtro por contexto — contextos derivados dos próprios registros. */
function useContextFilter(): ResourceFilters {
  const [context, setContext] = useState('')
  const tt = useDeclarativeText()
  const all = statusResource.useList({ per_page: 100 })
  const contexts = Array.from(new Set((all.data?.items ?? []).map((s) => s.context))).sort()
  return {
    params: { context: context || undefined },
    node: (
      <ToggleButtonGroup size="small" exclusive value={context} onChange={(_, v) => setContext(v ?? '')} aria-label={tt('Filtrar por contexto')}>
        <ToggleButton value="">{tt('Todos')}</ToggleButton>
        {contexts.map((c) => (
          <ToggleButton key={c} value={c}>{c}</ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
  }
}

// ── Motivos de não atendimento ───────────────────────────────────────
// Cadastro PRÓPRIO do Não atendido (Samuel, 04/09/2026): até então o diálogo de
// encerramento lia a lista de Desvios. É o catálogo universal filtrado no contexto
// `nao_atendido_motivo`, mantido pelo próprio usuário; o contexto fica fixo e fora
// do formulário.
const NAO_ATENDIDO_MOTIVO_CONTEXT = 'nao_atendido_motivo'

const motivoSchema = z.object({
  code: z.string().min(1, 'Informe o código'),
  label_pt: z.string().min(1, 'Informe o rótulo (pt)'),
  label_en: z.string().min(1, 'Informe o rótulo (en)'),
  sort_order: z.string().optional(),
  active: z.boolean(),
})

function MotivoFields() {
  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
        <RHFText name="code" label="Código" required placeholder="chuva_no_campo" />
        <RHFText name="sort_order" label="Ordem" type="number" />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <RHFText name="label_pt" label="Rótulo (pt)" required />
        <RHFText name="label_en" label="Rótulo (en)" required />
      </Box>
      <RHFSwitch name="active" label="Ativo" />
    </>
  )
}

const useMotivoFilters = (): ResourceFilters => ({ params: { context: NAO_ATENDIDO_MOTIVO_CONTEXT } })

export const naoAtendidoMotivosConfig: ResourceConfig<Status> = {
  title: 'Motivos de não atendimento', eyebrow: 'Categorias', exportPrefix: 'motivos-nao-atendimento',
  singular: 'motivo de não atendimento', basePath: '/logistica/categorias/nao-atendido-motivo',
  labelOf: (s) => s.label_pt || s.code,
  resource: statusResource, searchable: true, schema: motivoSchema, Fields: MotivoFields, useFilters: useMotivoFilters,
  formMaxWidth: 520,
  createPermission: 'master_data.create', editPermission: 'master_data.edit',
  deletePermission: 'master_data.delete',
  columns: [
    { key: 'code', header: 'Código', sortable: true },
    { key: 'label_pt', header: 'Rótulo (pt)', sortable: true },
    { key: 'label_en', header: 'Rótulo (en)' },
    { key: 'sort_order', header: 'Ordem', align: 'right', render: (s) => s.sort_order ?? '—' },
    { key: 'active', header: 'Situação', render: (s) => <StatusChip label={s.active === false ? 'Inativo' : 'Ativo'} tone={s.active === false ? 'neutral' : 'success'} /> },
  ],
  emptyForm: { code: '', label_pt: '', label_en: '', sort_order: '', active: true },
  toForm: (s) => ({ code: s.code, label_pt: s.label_pt, label_en: s.label_en, sort_order: s.sort_order != null ? String(s.sort_order) : '', active: s.active !== false }),
  toBody: (v) => ({ context: NAO_ATENDIDO_MOTIVO_CONTEXT, code: v.code, label_pt: v.label_pt, label_en: v.label_en, sort_order: v.sort_order === '' ? null : Number(v.sort_order), active: v.active }),
}

export const statusConfig: ResourceConfig<Status> = {
  title: 'Status', eyebrow: 'Categorias', exportPrefix: 'status',
  singular: 'status', basePath: '/logistica/categorias/status', labelOf: (s) => `${s.context}/${s.code}`,
  resource: statusResource, searchable: true, schema: statusSchema, Fields: StatusFields, useFilters: useContextFilter,
  createPermission: 'master_data.create', editPermission: 'master_data.edit',
  deletePermission: 'master_data.delete',
  columns: [
    { key: 'context', header: 'Contexto' },
    { key: 'code', header: 'Código' },
    { key: 'label_pt', header: 'Rótulo (pt)' },
    { key: 'label_en', header: 'Rótulo (en)' },
    { key: 'sort_order', header: 'Ordem', align: 'right', render: (s) => s.sort_order ?? '—' },
    { key: 'active', header: 'Situação', render: (s) => <StatusChip label={s.active === false ? 'Inativo' : 'Ativo'} tone={s.active === false ? 'neutral' : 'success'} /> },
  ],
  emptyForm: { context: '', code: '', label_pt: '', label_en: '', sort_order: '', active: true },
  toForm: (s) => ({ context: s.context, code: s.code, label_pt: s.label_pt, label_en: s.label_en, sort_order: s.sort_order != null ? String(s.sort_order) : '', active: s.active !== false }),
  toBody: (v) => ({ context: v.context, code: v.code, label_pt: v.label_pt, label_en: v.label_en, sort_order: v.sort_order === '' ? null : Number(v.sort_order), active: v.active }),
}
